import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import PermissionGate from '../../../components/common/PermissionGate';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Calendar,
  Clock,
  MapPin,
  Video,
  Edit,
  Trash2,
  Eye,
  XCircle,
  Download,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  X,
  CheckCircle,
  Square,
  List,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Send,
  Bell,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import Button from '../../../components/common/Button/Button';
import interviewService from '../../../api/services/interviewService';
import candidateService from '../../../api/services/candidateService';
import ScheduleInterviewModal from '../../../components/recruitment/ScheduleInterviewModal';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
const BACKEND_FETCH_LIMIT = 500;
const EMPTY_ARRAY = [];

const normalizeDateValue = (value) => {
  if (!value) return "";
  try {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split("T")[0];
    }
  } catch (_) { }
  if (typeof value === "string" && value.includes("T")) {
    return value.split("T")[0];
  }
  return typeof value === "string" ? value : "";
};

const normalizeTimeValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{2}:\d{2}/.test(value)) {
    return value.slice(0, 5);
  }
  return typeof value === "string" ? value : "";
};

const formatTimeAmPm = (timeStr) => {
  if (!timeStr) return "N/A";
  if (!timeStr.includes(':')) return timeStr;
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours, 10);
  if (isNaN(h)) return timeStr;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr12 = h % 12 || 12;
  return `${hr12}:${minutes.substring(0, 2)} ${ampm}`;
};

const InterviewDetailsModal = ({
  selectedInterview,
  setShowInterviewDetails,
  formatDate,
  getTypeIcon,
  getStatusColor,
  handleStatusChange,
  handleDelete,
  handleEditInterview,
  canEditInterview,
  canDeleteInterview,
  canMarkCompleted,
  handleSendReminder,
  isSendingReminder,
  isEmployeeView,
  user
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Interview Details
            </h2>
            <button
              onClick={() => setShowInterviewDetails(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Candidate
                  </h3>
                  <p className="text-lg font-semibold text-gray-900 capitalize break-all">
                    {selectedInterview?.candidateName}
                  </p>
                  <p className="text-sm text-gray-600 break-all">
                    {selectedInterview?.candidateEmail}
                  </p>
                  <p className="text-sm text-gray-600 capitalize break-all">
                    {selectedInterview?.jobTitle}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Interviewer
                  </h3>
                  <p className="text-lg font-semibold text-gray-900 capitalize break-all">
                    {selectedInterview?.interviewerName}
                  </p>
                  <p className="text-sm text-gray-600 break-all">
                    {selectedInterview?.interviewerEmail}
                  </p>
                </div>

                {selectedInterview?.createdByName && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">
                      Scheduled By
                    </h3>
                    <p className="text-sm font-semibold text-gray-900 capitalize">
                      {selectedInterview.createdByName}
                    </p>
                    {selectedInterview?.creatorEmail && (
                      <p className="text-sm text-gray-500 lowercase">
                        {selectedInterview.creatorEmail}
                      </p>
                    )}
                    {selectedInterview?.creatorDepartment && (
                      <p className="text-sm text-gray-500 capitalize italic">
                        {selectedInterview.creatorDepartment}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Schedule
                  </h3>
                  <div className="flex items-center space-x-2 mb-1">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm break-all">
                      {formatDate(selectedInterview?.date)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm break-all">
                      {formatTimeAmPm(selectedInterview?.time)} ({selectedInterview?.duration}{" "}
                      min)
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {React.createElement(getTypeIcon(selectedInterview?.type), { className: "h-4 w-4 text-gray-400" })}
                    <span className="text-sm capitalize break-all">
                      {selectedInterview?.type === 'in_person' ? selectedInterview?.location :
                        selectedInterview?.type === 'video' ? 'Video Call' :
                          selectedInterview?.location || 'Not specified'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">
                      Status
                    </h3>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(
                        selectedInterview?.status
                      )}`}
                    >
                      {selectedInterview?.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {selectedInterview?.notes && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Notes</h3>
                <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-lg border max-h-64 overflow-y-auto shadow-sm whitespace-pre-wrap break-words">
                  {selectedInterview.notes.split('\n').map((line, index) => {
                    const trimmedLine = line.trim();

                    // Check if line starts with a number followed by a dot (like "1.", "2.", etc.)
                    if (/^\d+\./.test(trimmedLine)) {
                      return (
                        <div key={index} className="mb-3">
                          <div className="font-bold text-gray-900 text-base mb-1 bg-blue-50 px-3 py-2 rounded-md border-l-4 border-blue-400 shadow-sm">
                            {trimmedLine}
                          </div>
                        </div>
                      );
                    }

                    // Check if line contains rating prompts or checkboxes
                    if (trimmedLine.includes('Rating (1-5)') || trimmedLine.includes('☐') || trimmedLine.includes('➡️')) {
                      return (
                        <div key={index} className="mb-2 pl-6">
                          <span className="text-gray-700 bg-yellow-50 px-2 py-1 rounded border-l-2 border-yellow-300">{line}</span>
                        </div>
                      );
                    }

                    // Check if line contains sub-points or bullet points
                    if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•') || trimmedLine.startsWith('*')) {
                      return (
                        <div key={index} className="mb-1 pl-6">
                          <span className="text-gray-700 bg-gray-100 px-2 py-1 rounded">{line}</span>
                        </div>
                      );
                    }

                    // Check if line contains colons (likely sub-headings)
                    if (trimmedLine.includes(':') && trimmedLine.length < 50) {
                      return (
                        <div key={index} className="mb-2 pl-4">
                          <span className="font-semibold text-gray-800 bg-green-50 px-2 py-1 rounded border-l-2 border-green-400">{line}</span>
                        </div>
                      );
                    }

                    // Check for specific keywords that should be highlighted
                    if (trimmedLine.includes('Strong') || trimmedLine.includes('Weak') || trimmedLine.includes('Good') || trimmedLine.includes('Excellent') || trimmedLine.includes('Average')) {
                      return (
                        <div key={index} className="mb-2">
                          <span className="font-medium text-gray-800 bg-green-50 px-3 py-1 rounded border-l-2 border-green-400">
                            {trimmedLine}
                          </span>
                        </div>
                      );
                    }

                    // Regular lines
                    if (trimmedLine.length > 0) {
                      return (
                        <div key={index} className="mb-1 pl-2">
                          <span className="text-gray-700">{line}</span>
                        </div>
                      );
                    }

                    // Empty lines
                    return <div key={index} className="mb-2"></div>;
                  })}
                </div>
              </div>
            )}

            {selectedInterview?.completionRemark?.remark && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Completion Remark</h3>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 shadow-sm">
                  <p className="text-sm text-gray-900 mb-3 whitespace-pre-wrap italic first-letter:capitalize">
                    {selectedInterview.completionRemark.remark}
                  </p>
                  <div className="flex flex-wrap justify-between items-center text-xs text-gray-500 gap-2 border-t border-green-100 pt-2">
                    <div className="flex items-center">
                      <span className="font-medium mr-1 text-green-700">Remarked by:</span>
                      <span className="capitalize">{
                        (selectedInterview.completionRemark.remarkerName ||
                          selectedInterview.completionRemark.remarkedBy?.fullName ||
                          selectedInterview.completionRemark.remarkedBy?.name ||
                          "HR Manager")
                      } {selectedInterview.completionRemark.remarkedBy?.department && `(${selectedInterview.completionRemark.remarkedBy.department})`}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      <span>{formatDate(selectedInterview.completionRemark.remarkedAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedInterview?.meetingLink && selectedInterview?.type === 'video' && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Meeting Link
                </h3>
                <a
                  href={selectedInterview.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 underline break-words"
                >
                  {selectedInterview.meetingLink}
                </a>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            {(['scheduled', 'rescheduled'].includes(selectedInterview?.status)) && (
              (isEmployeeView || user?.hasGranularPermission?.('recruitment', 'update')) ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    handleStatusChange(selectedInterview.id, "completed");
                    setShowInterviewDetails(false);
                  }}
                >
                  Mark Completed
                </Button>
              ) : (
                <PermissionGate permission="recruitment" action="update">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      handleStatusChange(selectedInterview.id, "completed");
                      setShowInterviewDetails(false);
                    }}
                  >
                    Mark Completed
                  </Button>
                </PermissionGate>
              )
            )}

            {['scheduled', 'rescheduled'].includes(selectedInterview?.status) && (
              (isEmployeeView || user?.hasGranularPermission?.('recruitment', 'update')) ? (
                <Button
                  variant="secondary"
                  icon={isSendingReminder ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  onClick={() => handleSendReminder(selectedInterview.id)}
                  className="text-amber-600 hover:text-amber-700 border-amber-200"
                  disabled={isSendingReminder}
                >
                  {isSendingReminder ? 'Sending...' : 'Send Reminder'}
                </Button>
              ) : (
                <PermissionGate permission="recruitment" action="update">
                  <Button
                    variant="secondary"
                    icon={isSendingReminder ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    onClick={() => handleSendReminder(selectedInterview.id)}
                    className="text-amber-600 hover:text-amber-700 border-amber-200"
                    disabled={isSendingReminder}
                  >
                    {isSendingReminder ? 'Sending...' : 'Send Reminder'}
                  </Button>
                </PermissionGate>
              )
            )}

            {selectedInterview?.status !== 'completed' && !isEmployeeView && (
              <PermissionGate permission="recruitment" action="update">
                <Button
                  variant="primary"
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                  onClick={() => handleEditInterview(selectedInterview.id)}
                >
                  Edit Interview
                </Button>
              </PermissionGate>
            )}
            <PermissionGate permission="recruitment" action="delete">
              <Button
                variant="secondary"
                className=""
                onClick={() => {
                  handleDelete(selectedInterview.id);
                  setShowInterviewDetails(false);
                }}
              >
                Delete Interview
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>
    </div>
  );
};

const InterviewScheduling = ({ isEmployeeView = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { user } = useAuth();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [showInterviewDetails, setShowInterviewDetails] = useState(false);
  const [editInterviewData, setEditInterviewData] = useState(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [searchInputValue, setSearchInputValue] = useState(searchParams.get('search') || '');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [filterType, setFilterType] = useState(searchParams.get('type') || 'all');
  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: searchParams.get('sortBy') || 'createdAt',
    direction: searchParams.get('sortOrder') || 'desc'
  });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] =
    useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] =
    useState(false);
  const selectedExportWrapRef = useRef(null);
  const [viewMode, setViewMode] = useState('list'); // calendar, list
  const [interviewToDelete, setInterviewToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Bulk delete state
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Completion Remark State
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkContent, setRemarkContent] = useState('');
  const [interviewIdForRemark, setInterviewIdForRemark] = useState(null);

  // Reminder Confirmation State
  const [showReminderConfirm, setShowReminderConfirm] = useState(false);
  const [interviewIdForReminder, setInterviewIdForReminder] = useState(null);

  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('limit')) || 10);

  const queryClient = useQueryClient();
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const isFirstMount = useRef(true);

  const {
    data: interviewsData,
    isLoading: loadingInterviews,
    error: interviewError,
    refetch: refetchInterviews
  } = useQuery({
    queryKey: ['interviews', searchTerm, filterStatus, filterType, dateRange, page, pageSize, isEmployeeView, user?.id || user?._id],
    queryFn: async () => {
      const mergedParams = {
        page,
        limit: pageSize,
        sortBy: sortConfig.key === 'date' ? 'scheduledDate' : sortConfig.key,
        sortOrder: sortConfig.direction,
        ...(isEmployeeView && (user?.id || user?._id) ? { interviewer: user?.id || user?._id } : {}),
        ...(filterStatus !== 'all' ? { status: filterStatus } : {}),
        ...(filterType !== 'all' ? { interviewType: filterType } : {}),
        ...(searchTerm ? { search: searchTerm } : {}),
        ...(dateRange.startDate ? { startDate: dateRange.startDate } : {}),
        ...(dateRange.endDate ? { endDate: dateRange.endDate } : {}),
      };
      const result = await interviewService.getInterviews(mergedParams);

      let rawInterviews = [];
      if (result?.data?.interviews) rawInterviews = result.data.interviews;
      else if (result?.interviews) rawInterviews = result.interviews;
      else if (Array.isArray(result)) rawInterviews = result;

      let mappedData = rawInterviews.map((interview) => {
        const creator = interview.createdBy || interview.creator;

        // Prioritize backend's already resolved name, email, and department
        const creatorName = interview.createdByName ||
          creator?.fullName || creator?.name ||
          (creator?.personalInfo?.firstName ? `${creator.personalInfo.firstName} ${creator.personalInfo.lastName || ""}`.trim() : null) ||
          (typeof creator === 'string' && creator.match(/^[0-9a-fA-F]{24}$/) ? `User (ID: ${creator.substring(0, 8)}...)` : (typeof creator === 'string' ? creator : "N/A"));

        const creatorEmail = interview.creatorEmail || creator?.email || creator?.personalInfo?.email || "";
        const creatorDepartment = interview.creatorDepartment || creator?.department || "";

        return {
          id: interview.id || interview._id,
          interviewId: interview.interviewId || `INT${Date.now()}`,
          candidateId: interview.candidateId || interview.candidate?._id,
          candidateName: interview.candidateName || `${interview.candidate?.firstName || ""} ${interview.candidate?.lastName || ""}`.trim() || "Unknown",
          candidateEmail: interview.candidateEmail || interview.candidate?.email || "",
          jobTitle: interview.jobTitle || interview.job?.title || "Unknown Job",
          jobId: interview.jobId || interview.job?._id || interview.job || "unknown",
          interviewerId: interview.interviewerId || interview.interviewer?._id,
          interviewerName: interview.interviewerName || `${interview.interviewer?.firstName || ""} ${interview.interviewer?.lastName || ""}`.trim() || "Unknown",
          interviewerEmail: interview.interviewerEmail || interview.interviewer?.email || "",
          date: normalizeDateValue(interview.scheduledDate || interview.date || interview.scheduleddatetime || interview.datetime),
          time: normalizeTimeValue(interview.scheduledTime || interview.time || interview.scheduled_time || interview?.scheduledDate?.split("T")[1] || interview?.date?.split?.("T")?.[1]),
          duration: interview.duration || 60,
          type: interview.interviewType || "video",
          status: interview.status || "scheduled",
          location: interview.location || "",
          meetingLink: interview.meetingLink || "",
          notes: interview.notes || "",
          round: interview.nextRound || "",
          completionRemark: interview.completionRemark || null,
          createdDate: interview.createdAt ? interview.createdAt.split("T")[0] : new Date().toISOString().split("T")[0],
          createdAt: interview.createdAt || new Date().toISOString(),
          createdBy: creator?._id || creator,
          createdByName: creatorName,
          creatorEmail: creatorEmail,
          creatorDepartment: creatorDepartment,
        };
      });

      return {
        items: mappedData,
        pagination: result?.data?.pagination || result?.pagination || null
      };
    },
    staleTime: 0,
    placeholderData: (previousData) => previousData,
  });

  // React Query: Fetch Shortlisted Candidates
  const { data: candidatesData, isLoading: loadingCandidates } = useQuery({
    queryKey: ['shortlisted-candidates'],
    queryFn: async () => {
      const result = await candidateService.getShortlistedCandidates();
      let candidatesData = [];
      if (result?.data?.candidates) candidatesData = result.data.candidates;
      else if (result?.data?.data?.candidates) candidatesData = result.data.data.candidates;
      else if (result?.candidates) candidatesData = result.candidates;
      else if (Array.isArray(result)) candidatesData = result;
      else if (result?.data && Array.isArray(result.data)) candidatesData = result.data;

      return candidatesData
        .filter(c => (c.recruitmentStatus || c.status || "").toLowerCase() === "shortlisted")
        .map(candidate => ({
          id: candidate.id || candidate._id,
          name: candidate.name || `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim() || "Unknown",
          email: candidate.email || "",
          jobTitle: candidate.jobTitle || "Unknown Job",
          jobId: candidate.jobId || candidate.job || "unknown",
          status: candidate.status || candidate.recruitmentStatus || "shortlisted",
        }));
    },
    enabled: showScheduleForm && !isEmployeeView && user?.role !== 'employee'
  });
  const candidates = candidatesData || EMPTY_ARRAY;

  // React Query: Update Status Mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, ...additionalData }) => interviewService.updateInterviewStatus(id, status, additionalData),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      toast.success(`Interview status updated to ${variables.status}`);
      if (selectedInterview && selectedInterview.id === variables.id) {
        setSelectedInterview(prev => ({ ...prev, status: variables.status }));
      }
    },
    onError: (err) => {
      toast.error('Failed to update status: ' + (err.message || 'Unknown error'));
    }
  });

  // React Query: Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => interviewService.deleteInterview(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      toast.success('Interview deleted successfully!');
      setShowDeleteConfirm(false);
      setInterviewToDelete(null);
      if (selectedInterview && selectedInterview.id === interviewToDelete) {
        setSelectedInterview(null);
        setShowInterviewDetails(false);
      }
    },
    onError: (err) => {
      toast.error('Failed to delete interview: ' + (err.message || 'Unknown error'));
    }
  });

  // React Query: Send Reminder Mutation
  const sendReminderMutation = useMutation({
    mutationFn: (id) => interviewService.sendInterviewReminder(id),
    onSuccess: (result) => {
      toast.success('Interview reminder sent successfully!');
    },
    onError: (err) => {
      toast.error('Failed to send reminder: ' + (err.message || 'Unknown error'));
    }
  });

  const handleSendReminder = (id) => {
    setInterviewIdForReminder(id);
    setShowReminderConfirm(true);
  };

  const confirmSendReminder = () => {
    if (interviewIdForReminder) {
      sendReminderMutation.mutate(interviewIdForReminder);
      setShowReminderConfirm(false);
      setInterviewIdForReminder(null);
    }
  };

  const cancelSendReminder = () => {
    setShowReminderConfirm(false);
    setInterviewIdForReminder(null);
  };

  const interviews = interviewsData?.items || EMPTY_ARRAY;
  const backendPagination = interviewsData?.pagination;

  const filteredInterviews = useMemo(() => {
    // Search and filtering are now handled by the backend
    let result = [...interviews];

    // Frontend sorting logic
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle case-insensitive string sorting
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        // Specific handling for dates if needed (already normalized strings in this case)
        if (sortConfig.key === 'date') {
          const dateA = new Date(`${a.date}T${a.time || '00:00'}`);
          const dateB = new Date(`${b.date}T${b.time || '00:00'}`);
          return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [interviews, sortConfig]);

  const totalItems = backendPagination?.totalInterviews || filteredInterviews.length;
  const totalPages = backendPagination?.totalPages || (totalItems === 0 ? 1 : Math.ceil(totalItems / pageSize));

  const paginatedInterviews = useMemo(() => {
    // With backend pagination, we don't slice locally.
    // If backend doesn't return pagination, fallback to slicing.
    if (backendPagination) {
      return filteredInterviews;
    }
    const startIndex = (page - 1) * pageSize;
    return filteredInterviews.slice(startIndex, startIndex + pageSize);
  }, [filteredInterviews, page, pageSize, backendPagination]);

  const baseExportFilters = useMemo(() => {
    const f = {};
    const trimmed = searchTerm?.trim();
    if (trimmed) f.search = trimmed;
    if (filterStatus !== 'all') f.status = filterStatus;
    if (filterType !== 'all') f.interviewType = filterType;
    if (dateRange.startDate) f.startDate = dateRange.startDate;
    if (dateRange.endDate) f.endDate = dateRange.endDate;
    if (isEmployeeView && (user?.id || user?._id)) {
      f.interviewer = user?.id || user?._id;
    }
    return f;
  }, [
    searchTerm,
    filterStatus,
    filterType,
    dateRange.startDate,
    dateRange.endDate,
    isEmployeeView,
    user?.id,
    user?._id,
  ]);

  const exportSortBy = useMemo(() => {
    if (sortConfig.key === 'date') return 'scheduledDate';
    if (sortConfig.key === 'type') return 'interviewType';
    return sortConfig.key;
  }, [sortConfig.key]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'interview-scheduling',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? page,
            limit: overrideLimit ?? pageSize,
            sortBy: exportSortBy,
            sortOrder: sortConfig.direction,
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackExt = format === 'xlsx' ? 'xlsx' : format;
      const fallbackName = `interview-scheduling-export.${fallbackExt}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, page, pageSize, exportSortBy, sortConfig.direction]
  );

  const handleInterviewSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows);
      if (!ids.length) {
        toast.error('No selected interviews to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} interview(s) exported!`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleInterviewScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !paginatedInterviews.length) {
          toast.error('No interviews to export on current page.');
          return;
        }

        if (scope === 'filtered' && totalItems === 0) {
          toast.error('No interviews match the current filters.');
          return;
        }

        const uid = user?.id || user?._id;
        const employeeOnlyFilters =
          isEmployeeView && uid ? { interviewer: uid } : {};

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: employeeOnlyFilters,
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      paginatedInterviews.length,
      totalItems,
      baseExportFilters,
      requestBackendExport,
      isEmployeeView,
      user?.id,
      user?._id,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Handle page overflow when filter/search changes
  useEffect(() => {
    if (interviewsData && page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [totalPages, page, interviewsData]);


  useEffect(() => {
  }, [user]);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'type', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (searchTerm) params.set('search', searchTerm);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterType && filterType !== 'all') params.set('type', filterType);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (page && page !== 1) params.set('page', String(page));
      if (pageSize && pageSize !== 10) params.set('limit', String(pageSize));
      if (sortConfig.key && sortConfig.key !== 'createdAt') params.set('sortBy', sortConfig.key);
      if (sortConfig.direction && sortConfig.direction !== 'desc') params.set('sortOrder', sortConfig.direction);

      return params;
    }, { replace: true });
  }, [searchTerm, filterStatus, filterType, dateRange, page, pageSize, sortConfig.key, sortConfig.direction, setSearchParams]);

  // Debounced search effect
  useEffect(() => {
    // Reset to first page on search/filter change
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(searchInputValue);
      setPage(1);
    }, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [filterStatus, filterType, searchInputValue, dateRange]);

  // Pagination effect
  useEffect(() => {
    // Page change handled by state, interviews from useQuery
  }, [page, pageSize]);

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const interviewTypes = [
    { id: "video", name: "Video Call", icon: Video },
    { id: "in_person", name: "In Person", icon: MapPin },
    // {id: "phone", name: "Phone Call", icon: Phone },
  ];


  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "rescheduled":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-3 w-3 ml-1 inline-block text-gray-400 group-hover:text-gray-600" />;
    return sortConfig.direction === 'asc' ?
      <ArrowUp className="h-3 w-3 ml-1 inline-block text-agrosaf-green" /> :
      <ArrowDown className="h-3 w-3 ml-1 inline-block text-agrosaf-green" />;
  };

  const getTypeIcon = (type) => {
    const typeConfig = interviewTypes.find((t) => t.id === type);
    return typeConfig ? typeConfig.icon : Video;
  };

  const getTypeLabel = (type) => {
    const typeConfig = interviewTypes.find((t) => t.id === type);
    return typeConfig ? typeConfig.name : (type || 'Unknown');
  };

  // Helper function to filter input to only alphanumeric, space, and comma with character limit
  const filterAlphanumericSpaceComma = (value, maxLength = Infinity) => {
    const filtered = value.replace(/[^a-zA-Z0-9\s,]/g, '');
    return maxLength !== Infinity ? filtered.slice(0, maxLength) : filtered;
  };

  // Character limits for each field
  const FIELD_LIMITS = {
    candidate: 150,
    interviewer: 150,
    meetingLink: 300
  };


  const handleStatusChange = (interviewId, newStatus) => {
    if (newStatus === 'completed') {
      setInterviewIdForRemark(interviewId);
      setShowRemarkModal(true);
      return;
    }
    updateStatusMutation.mutate({ id: interviewId, status: newStatus });
  };

  const handleRemarkSubmit = () => {
    if (!remarkContent.trim()) {
      toast.error('Please enter a remark');
      return;
    }

    updateStatusMutation.mutate({
      id: interviewIdForRemark,
      status: 'completed',
      completionRemark: {
        remark: remarkContent,
        remarkedAt: new Date().toISOString(),
        remarkedBy: user?.id || user?._id,
        remarkerName: user?.fullName || user?.name || "HR Manager"
      }
    });

    setShowRemarkModal(false);
    setRemarkContent('');
    setShowInterviewDetails(false);
  };

  const handleDelete = async (interviewId) => {
    setInterviewToDelete(interviewId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (!interviewToDelete) return;
    deleteMutation.mutate(interviewToDelete);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setInterviewToDelete(null);
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setBulkDeleteLoading(true);
    const ids = Array.from(selectedRows);
    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        const result = await interviewService.deleteInterview(id);
        if (result && (result.success || result.responseCode === 200)) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }
    setBulkDeleteLoading(false);
    setShowBulkDeleteConfirm(false);
    setSelectedRows(new Set());
    if (successCount > 0) {
      toast.success(`${successCount} interview(s) deleted successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
      refetchInterviews();
    } else {
      toast.error('Failed to delete selected interviews');
    }
  };

  const handleEditInterview = (interviewId) => {
    const interviewToEdit = interviews.find(i => i.id === interviewId);
    if (interviewToEdit) {
      setEditInterviewData(interviewToEdit);
      setShowScheduleForm(true);
      setShowInterviewDetails(false);
    }
  };

  // Granular permission-based access control (using PermissionGate component instead)
  const canEditInterview = () => {
    // This is kept for backward compatibility with existing code
    // But PermissionGate should be used directly in JSX
    return true; // PermissionGate will handle the actual check
  };

  const canDeleteInterview = () => {
    // This is kept for backward compatibility with existing code
    // But PermissionGate should be used directly in JSX
    return true; // PermissionGate will handle the actual check
  };

  const canMarkCompleted = () => {
    // If user role is not set, allow marking completed for testing
    if (!user?.role) {
      return true;
    }
    const canMark =
      user?.role === "hr_manager" ||
      user?.role === "super_admin" ||
      user?.role === "recruiter" ||
      user?.role === "interviewer" ||
      isEmployeeView;
    return canMark;
  };

  const clearFilters = () => {
    setSearchInputValue('');
    setFilterStatus('all');
    setFilterType('all');
    setDateRange({ startDate: '', endDate: '' });
    setSelectedRows(new Set());
    setPage(1);
  };

  // Selection handlers
  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = paginatedInterviews.map(i => i.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const getInterviewsForDate = (date) => {
    return interviews.filter(interview => interview.date === date);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return parsed.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };



  // Error state with fallback
  if (interviewError && interviews.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Interview Scheduling
            </h1>
            <p className="text-gray-600 mt-1">
              Schedule and manage candidate interviews
            </p>
          </div>
            {!isEmployeeView && (
              <PermissionGate permission="recruitment" action="create">
                <Button
                  variant="primary"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => setShowScheduleForm(true)}
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                  disabled={loadingInterviews}
                >
                  Schedule Interview
                </Button>
              </PermissionGate>
            )}
        </div>

        {/* Error Message */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-600 mb-4">
            <AlertCircle className="h-12 w-12 mx-auto mb-2" />
            <h3 className="text-lg font-semibold">Error Loading Interviews</h3>
            <p className="text-sm">{interviewError?.message || 'Unknown error'}</p>
          </div>
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => refetchInterviews()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>

        {/* Empty State */}
        <div className="text-center py-12">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No interviews found
          </h3>
          <p className="text-gray-500 mb-4">
            Get started by scheduling your first interview.
          </p>
          {!isEmployeeView && (
            <PermissionGate permission="recruitment" action="create">
              <Button
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => setShowScheduleForm(true)}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                Schedule Interview
              </Button>
            </PermissionGate>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Interview Scheduling
          </h1>
          <p className="text-gray-600 mt-1">
            Schedule and manage candidate interviews
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <div className="flex items-center bg-gray-100 rounded-lg p-1 mr-2">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-agrosaf-green' : 'text-gray-500 hover:text-gray-700'}`}
              title="List View"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-2 rounded-md transition-all ${viewMode === 'calendar' ? 'bg-white shadow-sm text-agrosaf-green' : 'text-gray-500 hover:text-gray-700'}`}
              title="Calendar View"
            >
              <Calendar className="h-4 w-4" />
            </button>
          </div>
          {selectedRows.size > 0 && (
            <PermissionGate permission="recruitment" action="delete">
              <Button
                variant="danger"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setShowBulkDeleteConfirm(true)}
                loading={bulkDeleteLoading}
              >
                Delete Selected ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="recruitment" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" /> : <Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className={selectedRows.size > 0 ? "bg-blue-50 border-blue-200 text-blue-600" : ""}
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : "Export"}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleInterviewSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          {!isEmployeeView && (
            <PermissionGate permission="recruitment" action="create">
              <Button
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => {
                  setShowScheduleForm(true);
                }}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                disabled={loadingInterviews}
              >
                Schedule Interview
              </Button>
            </PermissionGate>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by candidate name or interviewer..."
              value={searchInputValue}
              maxLength={100}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                let value = e.target.value;
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                value = value.replace(/[^a-zA-Z0-9\s@._+-]/g, '');
                // Enforce max length
                if (value.length > 100) {
                  value = value.substring(0, 100);
                }
                setSearchInputValue(value);
              }}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
            {searchInputValue && (
              <button
                onClick={() => {
                  setSearchInputValue('');
                  // Maintain focus after clearing
                  setTimeout(() => {
                    if (searchInputRef.current) {
                      searchInputRef.current.focus();
                    }
                  }, 50);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="rescheduled">Rescheduled</option>
            </select>
          </div>

          {/* Type Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Types</option>
              <option value="video">Video Call</option>
              <option value="in_person">In Person</option>
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={setDateRange}
            />
          </div>

          {/* Clear Filters */}
          {(searchInputValue || filterStatus !== 'all' || filterType !== 'all' || dateRange.startDate || dateRange.endDate) && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={clearFilters}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {currentDate.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    setCurrentDate(
                      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
                    )
                  }
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  Today
                </button>
                <button
                  onClick={() =>
                    setCurrentDate(
                      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
                    )
                  }
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-7 gap-4">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <div
                  key={day}
                  className="text-center text-sm font-medium text-gray-500 py-2"
                >
                  {day}
                </div>
              ))}
              {(() => {
                const year = currentDate.getFullYear();
                const month = currentDate.getMonth();
                const firstDay = new Date(year, month, 1);
                const monthDays = new Date(year, month + 1, 0).getDate();
                const startOffset = (firstDay.getDay() + 6) % 7; // Monday=0
                const totalCells = Math.ceil((startOffset + monthDays) / 7) * 7;

                return Array.from({ length: totalCells }, (_, cellIndex) => {
                  if (cellIndex < startOffset) {
                    return (
                      <div
                        key={`empty-${cellIndex}`}
                        className="min-h-[120px] border border-dashed border-gray-100 rounded-lg p-2 bg-gray-50/50"
                      />
                    );
                  }

                  const day = cellIndex - startOffset + 1;
                  if (day > monthDays) {
                    return (
                      <div
                        key={`empty-${cellIndex}`}
                        className="min-h-[120px] border border-dashed border-gray-100 rounded-lg p-2 bg-gray-50/50"
                      />
                    );
                  }
                  const date = new Date(year, month, day);
                  const dateString = date.toISOString().split("T")[0];
                  const dayInterviews = getInterviewsForDate(dateString);

                  return (
                    <div
                      key={dateString}
                      className="min-h-[120px] border border-gray-200 rounded-lg p-2"
                    >
                      <div className="text-sm font-medium text-gray-900 mb-2">
                        {day}
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                        {dayInterviews.map((interview) => (
                          <div
                            key={interview.id}
                            className={`relative group rounded-lg border p-2 transition-all ${selectedRows.has(interview.id) ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-100'}`}
                          >
                            <div className="flex items-start space-x-2">
                              <input
                                type="checkbox"
                                checked={selectedRows.has(interview.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectRow(interview.id);
                                }}
                                className="mt-1 h-3 w-3 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded cursor-pointer"
                              />
                              <div
                                onClick={() => {
                                  setSelectedInterview(interview);
                                  setShowInterviewDetails(true);
                                }}
                                className="flex-1 cursor-pointer min-w-0"
                              >
                                <div className="text-xs font-semibold text-gray-900 truncate capitalize block">
                                  {interview.candidateName}
                                </div>
                                <div className="text-[10px] text-gray-500 truncate">{formatTimeAmPm(interview.time)}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )
      }

      {/* Interviews List */}
      {
        viewMode === 'list' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        checked={paginatedInterviews.length > 0 && selectedRows.size === paginatedInterviews.length}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[200px] cursor-pointer group hover:bg-gray-100 transition-colors"
                      onClick={() => requestSort('candidateName')}
                    >
                      <div className="flex items-center">
                        Candidate
                        {renderSortIcon('candidateName')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                      onClick={() => requestSort('interviewerName')}
                    >
                      <div className="flex items-center">
                        Interviewer
                        {renderSortIcon('interviewerName')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                      onClick={() => requestSort('date')}
                    >
                      <div className="flex items-center">
                        Date & Time
                        {renderSortIcon('date')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                      onClick={() => requestSort('type')}
                    >
                      <div className="flex items-center">
                        Type
                        {renderSortIcon('type')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                      onClick={() => requestSort('status')}
                    >
                      <div className="flex items-center">
                        Status
                        {renderSortIcon('status')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                      onClick={() => requestSort('createdAt')}
                    >
                      <div className="flex items-center">
                        Created At
                        {renderSortIcon('createdAt')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingInterviews ? (
                    [...Array(5)].map((_, index) => (
                      <tr key={`skeleton-${index}`} className="animate-pulse">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-4 bg-gray-200 rounded w-4"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="h-4 w-4 bg-gray-200 rounded mr-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end space-x-2">
                            <div className="h-8 w-8 bg-gray-200 rounded"></div>
                            <div className="h-8 w-8 bg-gray-200 rounded"></div>
                            <div className="h-8 w-8 bg-gray-200 rounded"></div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    paginatedInterviews.map((interview) => (
                      <tr key={interview.id} className={`hover:bg-gray-50 ${selectedRows.has(interview.id) ? 'bg-blue-50/50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(interview.id)}
                            onChange={() => handleSelectRow(interview.id)}
                            className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 overflow-hidden w-[200px] max-w-[200px]">
                          <div className="min-w-0 w-full">
                            <div
                              className="text-sm font-medium text-gray-900 truncate block capitalize"
                              title={interview.candidateName}
                            >
                              {interview.candidateName}
                            </div>
                            <div
                              className="text-sm text-gray-500 truncate block capitalize"
                              title={interview.jobTitle}
                            >
                              {interview.jobTitle}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                          <div className="max-w-[180px] truncate" title={interview.interviewerName}>
                            {interview.interviewerName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(interview.date)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatTimeAmPm(interview.time)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {React.createElement(getTypeIcon(interview.type), {
                              className: "h-4 w-4 text-gray-400 mr-2",
                            })}
                            <span className="text-sm text-gray-900 capitalize">
                              {getTypeLabel(interview.type)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(
                              interview.status
                            )}`}
                          >
                            {interview.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="text-sm text-gray-900">
                            {formatDate(interview.createdAt)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(interview.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {(isEmployeeView || user?.hasGranularPermission?.('recruitment', 'read')) ? (
                              <button
                                onClick={() => {
                                  setSelectedInterview(interview);
                                  setShowInterviewDetails(true);
                                }}
                                className="text-blue-600 hover:text-blue-900"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            ) : (
                              <PermissionGate permission="recruitment" action="read">
                                <button
                                  onClick={() => {
                                    setSelectedInterview(interview);
                                    setShowInterviewDetails(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="View Details"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                            {['scheduled', 'rescheduled'].includes(interview.status) && (
                              (isEmployeeView || user?.hasGranularPermission?.('recruitment', 'update')) ? (
                                <button
                                  onClick={() => handleSendReminder(interview.id)}
                                  className={`text-amber-600 hover:text-amber-900 ${(sendReminderMutation.isLoading || sendReminderMutation.isPending) && sendReminderMutation.variables === interview.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  title="Send Reminder"
                                  disabled={(sendReminderMutation.isLoading || sendReminderMutation.isPending) && sendReminderMutation.variables === interview.id}
                                >
                                  {(sendReminderMutation.isLoading || sendReminderMutation.isPending) && sendReminderMutation.variables === interview.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Send className="h-4 w-4" />
                                  )}
                                </button>
                              ) : (
                                <PermissionGate permission="recruitment" action="update">
                                  <button
                                    onClick={() => handleSendReminder(interview.id)}
                                    className={`text-amber-600 hover:text-amber-900 ${(sendReminderMutation.isLoading || sendReminderMutation.isPending) && sendReminderMutation.variables === interview.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    title="Send Reminder"
                                    disabled={(sendReminderMutation.isLoading || sendReminderMutation.isPending) && sendReminderMutation.variables === interview.id}
                                  >
                                    {(sendReminderMutation.isLoading || sendReminderMutation.isPending) && sendReminderMutation.variables === interview.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Send className="h-4 w-4" />
                                    )}
                                  </button>
                                </PermissionGate>
                              )
                            )}
                            {interview.status !== 'completed' && (
                              <>
                                {['scheduled', 'rescheduled'].includes(interview.status?.toLowerCase()) && canMarkCompleted() && (
                                  (isEmployeeView || user?.hasGranularPermission?.('recruitment', 'update')) ? (
                                    <button
                                      onClick={() => handleStatusChange(interview.id, 'completed')}
                                      className="text-green-600 hover:text-green-900"
                                      title="Mark Completed"
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </button>
                                  ) : (
                                    <PermissionGate permission="recruitment" action="update">
                                      <button
                                        onClick={() => handleStatusChange(interview.id, 'completed')}
                                        className="text-green-600 hover:text-green-900"
                                        title="Mark Completed"
                                      >
                                        <CheckCircle className="h-4 w-4" />
                                      </button>
                                    </PermissionGate>
                                  )
                                )}
                                {!isEmployeeView && (
                                  <PermissionGate permission="recruitment" action="update">
                                    <button
                                      onClick={() => handleEditInterview(interview.id)}
                                      className="text-indigo-600 hover:text-indigo-900"
                                      title="Edit"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                  </PermissionGate>
                                )}
                              </>
                            )}
                            <PermissionGate permission="recruitment" action="delete">
                              <button
                                onClick={() => handleDelete(interview.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table >
            </div >
          </div >
        )
      }

      {/* Pagination Control */}
      {
        totalPages > 0 && totalItems > 0 && (
          <PaginationControls
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            limit={pageSize}
            hasNextPage={page < totalPages}
            hasPrevPage={page > 1}
            onPageChange={(p) => setPage(p)}
            onLimitChange={(l) => { setPageSize(Number(l)); setPage(1); }}
          />
        )
      }

      {/* Empty State */}
      {
        !loadingInterviews && interviews.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No interviews found
            </h3>
            <p className="text-gray-500 mb-4">
              {searchInputValue || filterStatus !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by scheduling your first interview.'
              }
            </p>
            {!searchInputValue && filterStatus === 'all' && !isEmployeeView && (
              <PermissionGate permission="recruitment" action="create">
                <Button
                  variant="primary"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => setShowScheduleForm(true)}
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                >
                  Schedule Interview
                </Button>
              </PermissionGate>
            )}
          </div>
        )
      }

      {/* Modals */}
      <ScheduleInterviewModal
        isOpen={showScheduleForm}
        onClose={() => {
          setShowScheduleForm(false);
          setEditInterviewData(null);
        }}
        onSuccess={(result) => {
          queryClient.invalidateQueries({ queryKey: ['interviews'] });
        }}
        candidates={candidates}
        editData={editInterviewData}
      />
      {
        showInterviewDetails && <InterviewDetailsModal
          selectedInterview={selectedInterview}
          setShowInterviewDetails={setShowInterviewDetails}
          formatDate={formatDate}
          getTypeIcon={getTypeIcon}
          getStatusColor={getStatusColor}
          handleStatusChange={handleStatusChange}
          handleDelete={handleDelete}
          handleEditInterview={handleEditInterview}
          canEditInterview={canEditInterview}
          canDeleteInterview={canDeleteInterview}
          canMarkCompleted={canMarkCompleted}
          handleSendReminder={handleSendReminder}
          isSendingReminder={sendReminderMutation.isLoading || sendReminderMutation.isPending}
          isEmployeeView={isEmployeeView}
          user={user}
        />
      }


      {/* Delete Confirmation Modal */}
      {
        showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Delete Interview
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to delete this interview? This action
                  cannot be undone.
                </p>
                <div className="flex space-x-3">
                  <Button
                    variant="secondary"
                    onClick={cancelDelete}
                    className="flex-1"
                    disabled={deleteMutation.isLoading}
                  >
                    No
                  </Button>
                  <PermissionGate permission="recruitment" action="delete">
                    <Button
                      variant="danger"
                      onClick={confirmDelete}
                      className="flex-1"
                      disabled={deleteMutation.isLoading}
                    >
                      {deleteMutation.isLoading ? "Deleting..." : "Yes"}
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Delete Confirmation Modal */}
      {
        showBulkDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Delete Selected Interviews
                </h3>
                <p className="text-gray-600 text-center mb-2">
                  You are about to permanently delete{' '}
                  <span className="font-semibold text-red-600">{selectedRows.size} interview(s)</span>.
                </p>
                <p className="text-sm text-gray-500 text-center mb-6">
                  This action cannot be undone.
                </p>
                <div className="flex space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowBulkDeleteConfirm(false)}
                    className="flex-1"
                    disabled={bulkDeleteLoading}
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="recruitment" action="delete">
                    <Button
                      variant="danger"
                      onClick={handleBulkDelete}
                      className="flex-1"
                      loading={bulkDeleteLoading}
                    >
                      Delete {selectedRows.size} Interview(s)
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Interview Schedule"
        showDateRange
        scopedFormatExport={{
          onExport: handleInterviewScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Reminder Confirmation Modal */}
      {
        showReminderConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full">
                  <Send className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Send Interview Reminder
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to send a reminder email to the candidate and interviewer for this interview?
                </p>
                <div className="flex space-x-3">
                  <Button
                    variant="secondary"
                    onClick={cancelSendReminder}
                    className="flex-1"
                    disabled={sendReminderMutation.isLoading || sendReminderMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={confirmSendReminder}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                    disabled={sendReminderMutation.isLoading || sendReminderMutation.isPending}
                  >
                    {sendReminderMutation.isLoading || sendReminderMutation.isPending ? "Sending..." : "Yes, Send"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Remark Modal */}
      {
        showRemarkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Complete Interview Remark
                  </h3>
                  <button
                    onClick={() => setShowRemarkModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Please provide a remark about this interview.
                </p>
                <div className="relative mb-4">
                  <textarea
                    value={remarkContent}
                    onChange={(e) => {
                      const filteredValue = e.target.value.replace(/[^a-zA-Z0-9\s.,?!@#$%^&*()-_+:=;'"/[\]{}]/g, '');
                      if (filteredValue.length <= 500) {
                        setRemarkContent(filteredValue);
                      }
                    }}
                    placeholder="Enter remark..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent min-h-[100px]"
                    rows={4}
                  />
                  <div className="flex justify-end mt-1">
                    <span className={`text-xs ${remarkContent.length >= 500 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                      {remarkContent.length}/500
                    </span>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowRemarkModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleRemarkSubmit}
                    className="flex-1 bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white"
                    disabled={updateStatusMutation.isLoading || updateStatusMutation.isPending}
                  >
                    {updateStatusMutation.isLoading || updateStatusMutation.isPending ? "Submitting..." : "Submit Remark"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default InterviewScheduling;
